'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { LoginDialog } from '../(main)/store/components/fb-login';
import Chat from '../chat/page';
import { useChatThread } from '@/hooks/use-chat-thread';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

interface AmoriSidebarProps {
  className?: string;
  defaultCollapsed?: boolean;
}

/**
 * Amori Sidebar - Desktop persistent chat widget
 * Collapsible sidebar for Amori AI chat on desktop
 */
export default function AmoriSidebar({
  className,
  defaultCollapsed = false,
}: AmoriSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const { threadId, restartThread } = useChatThread(
    user?.cunstomerId ?? null,
    null
  );
  // Trước đây fallback 'ha-noi' khi URL không có segment city — tức ở trang chủ
  // rootless, mọi link trong sidebar đều ném user sang Hà Nội.
  const routes = useCityRoutes();

  useEffect(() => {
    if (isAuthenticated) setShowLoginDialog(false);
  }, [isAuthenticated]);

  const quickActions = [
    {
      id: 'for-you',
      label: 'FOR YOU',
      sublabel: 'AI gợi ý thỏa sở thích của bạn',
      image: '/static/images/for-you-header.webp',
      href: routes.listing('cho-ban'),
      gradient: 'from-secondary-10 to-secondary-20',
      textColor: 'text-[#0E4174]',
      sublabelColor: 'text-neutral-70',
    },
    {
      id: 'solomeal',
      label: 'SOLOMEAL',
      sublabel: 'Bữa ăn siêu ngon Miễn phí vận chuyển',
      image: '/static/images/solomeal-quick-search.webp',
      href: routes.solomeal,
      gradient: 'from-[#FFF5E8] to-[#FFE4C4]',
      textColor: 'text-[#0E4174]',
      sublabelColor: 'text-neutral-70',
    },
    {
      id: 'trending',
      label: 'XU HƯỚNG',
      sublabel: 'Món ngon đang được yêu thích',
      image: '/static/images/trending-header.webp',
      href: routes.listing('xu-huong'),
      gradient: 'from-green-20 to-green-40',
      textColor: 'text-[#0E4174]',
      sublabelColor: 'text-neutral-70',
    },
  ];

  const UnAuthen = () => (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
      <Avatar className="size-24">
        <AvatarImage
          src={`${assetPrefix}/static/images/robot-cook.webp`}
          alt="AI Bot"
        />
        <AvatarFallback>AI</AvatarFallback>
      </Avatar>
      <div className="text-center">
        <h3 className="text-lg font-bold text-neutral-90 mb-2">
          Chat với Amori AI
        </h3>
        <p className="text-sm text-neutral-60 mb-4">
          Đăng nhập để nhận gợi ý món ăn từ AI
        </p>
      </div>
      <Button
        size="sm"
        className="bg-primary text-white rounded-full px-6"
        onClick={() => setShowLoginDialog(true)}
      >
        Đăng nhập
      </Button>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto scrollbar-hide">
      <Card
        className={cn(
          'flex flex-col bg-white/30 border border-white/90 shadow-lg transition-all duration-300 overflow-hidden rounded-2xl',
          isCollapsed ? 'h-16' : 'h-fit min-h-[600px] max-h-full',
          className
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between p-4 text-secondary-60 cursor-pointer',
            isCollapsed && 'rounded-lg'
          )}
          onClick={() => setIsCollapsed(!isCollapsed)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsCollapsed(!isCollapsed);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed ? 'Mở rộng chat Amori AI' : 'Thu gọn chat Amori AI'
          }
        >
          <div className="flex items-center gap-3">
            <Avatar className="size-8">
              <AvatarImage
                src={`${assetPrefix}/static/images/new-bot-avt.webp`}
                alt="Amori AI"
              />
              <AvatarFallback>AI</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-bold text-sm">Amori AI</h3>
              {!isCollapsed && (
                <p className="text-xs opacity-90">Trợ lý ẩm thực thông minh</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isCollapsed && isAuthenticated && threadId && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent('chatbot_event', {
                    action_time: Math.floor(Date.now() / 1000),
                    button_name: 'chatbot_restart_sidebar',
                    timestamp: Date.now(),
                    user_id: user?.cunstomerId,
                    thread_id: threadId,
                  });
                  restartThread();
                }}
              >
                <RotateCcw className="size-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="size-8">
              {isCollapsed ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Chat Content */}
        {!isCollapsed && (
          <div className="flex flex-col h-full overflow-auto">
            {isAuthenticated && threadId ? (
              <Chat
                key={threadId}
                threadId={threadId}
                className="flex-1 scrollbar-hide"
              />
            ) : isLoading ? (
              <div className="flex items-center justify-center h-full text-sm text-neutral-60">
                Đang kiểm tra đăng nhập...
              </div>
            ) : (
              <UnAuthen />
            )}

            {/* Quick Actions */}
          </div>
        )}
      </Card>

      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="amori-sidebar"
      />
      {isAuthenticated && (
        <div className="pb-4 pt-2 space-y-3">
          {quickActions.map((action) => (
            <Link
              key={action.id}
              href={action.href}
              onClick={() => {
                trackEvent('click_button', {
                  action_time: Math.floor(Date.now() / 1000),
                  button_name: `amori_sidebar_${action.id}`,
                  button_location: 'amori_sidebar',
                  user_id: user?.cunstomerId,
                });
              }}
              className={cn(
                'group relative block rounded-2xl overflow-hidden',
                'bg-white/50 shadow-md hover:shadow-lg border border-white/70',
                'transition-all duration-300',
                action.gradient
              )}
            >
              <div className="flex items-center justify-between p-4 pr-2">
                {/* Text Content */}
                <div className="flex-1 space-y-1">
                  <h4
                    className={cn(
                      'font-bold text-base leading-tight',
                      action.textColor
                    )}
                  >
                    {action.label}
                  </h4>
                  <p
                    className={cn(
                      'text-sm leading-tight whitespace-pre-line',
                      action.sublabelColor
                    )}
                  >
                    {action.sublabel}
                  </p>
                </div>

                {/* Image */}
                <div className="relative shrink-0">
                  <Image
                    src={action.image}
                    alt={action.label}
                    width={120}
                    height={80}
                    className="object-contain"
                    sizes="96px"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
